import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { StatusLED } from "@/components/infra";
import { LogoMark } from "@/components/hero/HeroShared";
import { BHeroCopy, B_GRID_BG } from "@/components/hero/BHeroCopy";
import { timeLabel } from "@/components/hero/LiveIndex";
import { StatsVisualStrip } from "@/components/hero/StatVisuals";
import { useHeroLiveStats } from "@/lib/useHeroLiveStats";

const TickerItem = ({ stock }) => {
  const up = stock.change_pct >= 0;
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-2.5 px-6 whitespace-nowrap border-r border-white/10">
      <span className="font-mono-label text-[11px] !tracking-normal text-white">{stock.symbol}</span>
      <span className="text-xs text-[#9CA1AA]">{stock.name}</span>
      <span className="text-xs text-[#D7DAE0]">${Number(stock.price).toFixed(2)}</span>
      <span className={`inline-flex items-center text-xs font-semibold ${up ? "text-[#34D399]" : "text-[#FF7A85]"}`}><Arrow size={12} aria-hidden="true" />{up ? "+" : "−"}{Math.abs(stock.change_pct).toFixed(2)}%</span>
    </span>
  );
};

const TickerBand = ({ stocks, prefix }) => (
  <div data-testid={`${prefix}-ticker`} className="relative bg-[#0F1117] text-white overflow-hidden" role="region" aria-label="Live digital infrastructure stocks">
    <div className="flex items-stretch">
      <span className="shrink-0 z-10 inline-flex items-center gap-2 px-4 sm:px-6 py-3.5 bg-[#0F1117] border-r border-white/15 font-mono-label text-[10px] !tracking-normal text-[#34D399]"><StatusLED color="#10B981" />Infra stocks</span>
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="hero-ticker flex w-max py-3.5">
          <div className="flex">{stocks.map((stock) => <TickerItem key={stock.symbol} stock={stock} />)}</div>
          <div className="flex" aria-hidden="true">{stocks.map((stock) => <TickerItem key={`copy-${stock.symbol}`} stock={stock} />)}</div>
        </div>
      </div>
    </div>
  </div>
);

export const HeroOptionB4 = ({ prefix = "hero-option-b4", sectionId, testId = prefix, rings = true, stats = true, minimal = false }) => {
  const live = useHeroLiveStats();
  return (
    <section id={sectionId} data-testid={testId} className="relative overflow-hidden border-b hairline bg-white">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.5]" style={B_GRID_BG} />
      <div className={`relative max-w-7xl mx-auto px-6 md:px-12 pt-12 sm:pt-16 lg:pt-16 ${stats ? "pb-10" : "pb-14 sm:pb-16 lg:pb-20"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-12 items-center">
          <BHeroCopy prefix={prefix} brand={!minimal} eyebrow={minimal ? null : "Tracking the industry behind AI"} className="lg:col-span-7" />
          <div className={`lg:col-span-5 lg:order-first ${minimal ? "hidden sm:flex" : "flex"} justify-center py-10 sm:py-6 fade-up delay-200`}>
            <div className="hero-a-stage relative">
              {rings && <>
                <span aria-hidden="true" className="hero-a-bloom hidden sm:block" />
                <span aria-hidden="true" className="hero-a-ring" />
                <span aria-hidden="true" className="hero-a-ring hero-a-orbit hidden sm:block" />
              </>}
              <LogoMark testId={`${prefix}-mark`} className="relative w-[200px] sm:w-[260px] xl:w-[320px] dcyb-mark-shadow dcyb-mark-float" />
            </div>
          </div>
        </div>
        {stats && <div className="mt-14 lg:mt-12"><StatsVisualStrip prefix={prefix} jobs={live.jobs} updated={timeLabel(live.updatedAt)} /></div>}
      </div>
      {stats && live.allStocks.length > 0 && <TickerBand prefix={prefix} stocks={live.allStocks} />}
    </section>
  );
};
